import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnalyticsStore, ProjectStore } from "stores";
import { Button, Card, Table } from "semantic-ui-react";
import { displayTimeLineChart } from "core/charts";
import Spinner from "components/spinner";
import { TestHistoryChartData } from "models/analytics";
import { TestStatus } from "models/test-run";
import { numberWithCommas, organizationUrl } from "core/utils";
import ProjectSelect from "components/form/project-select";
import ProjectTestSelect from "components/form/project-test-select";
import Select from "components/form/select";
import RepoLink from "components/repo-link";
import { useInitialEffect } from "core/react-utils";
import { format } from "date-fns";

type SortColumn = "startTime" | "status" | "duration" | "environment" | "branch";
type SortDirection = "ascending" | "descending";

const emptyData = new TestHistoryChartData();

type DateRange = "day" | "month" | "quarter" | "year" | null;

const dateRangeOptions = [
  { label: "Last Day", value: "day" },
  { label: "Last Month", value: "month" },
  { label: "Last Quarter", value: "quarter" },
  { label: "Last Year", value: "year" },
];

const ROWS_PER_PAGE = 20;

const TestHistoryTab = observer(() => {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [projectTestId, setProjectTestId] = useState<number | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("startTime");
  const [sortDirection, setSortDirection] = useState<SortDirection>("descending");
  const [environmentFilter, setEnvironmentFilter] = useState<string | null>(null);
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>("month");
  const [currentPage, setCurrentPage] = useState(1);

  const statusOptions = Object.keys(TestStatus)
    .filter(s => s !== "FLAKY")
    .map(s => ({
      label: s.replace("_", " "),
      value: s,
    }));
  const chartData = AnalyticsStore.testHistoryChartData;
  const data = chartData.data || emptyData;
  const selectedProject = projectId ? ProjectStore.find(projectId) : null;

  const uniqueEnvironments = useMemo(() => {
    if (!data.results) return [];
    const environments = [...new Set(data.results.map(r => r.environment).filter(Boolean))];
    return environments.sort().map(env => ({ label: env, value: env }));
  }, [data.results]);

  const uniqueBranches = useMemo(() => {
    if (!data.results) return [];
    const branches = [...new Set(data.results.map(r => r.branch).filter(Boolean))];
    return branches.sort().map(branch => ({ label: branch, value: branch }));
  }, [data.results]);

  const filteredResults = useMemo(() => {
    if (!data.results || data.results.length === 0) return [];

    return data.results.filter(r => {
      if (environmentFilter && r.environment !== environmentFilter) return false;
      if (branchFilter && r.branch !== branchFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      return true;
    });
  }, [data.results, environmentFilter, branchFilter, statusFilter]);

  const sortedResults = useMemo(() => {
    if (filteredResults.length === 0) return [];

    return [...filteredResults].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case "startTime":
          const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "duration":
          comparison = (a.duration || 0) - (b.duration || 0);
          break;
        case "environment":
          comparison = (a.environment || "").localeCompare(b.environment || "");
          break;
        case "branch":
          comparison = (a.branch || "").localeCompare(b.branch || "");
          break;
      }

      return sortDirection === "ascending" ? comparison : -comparison;
    });
  }, [filteredResults, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedResults.length / ROWS_PER_PAGE);

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    return sortedResults.slice(startIndex, startIndex + ROWS_PER_PAGE);
  }, [sortedResults, currentPage]);

  // Reset to first page when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [environmentFilter, branchFilter, statusFilter, sortColumn, sortDirection, dateRange, projectTestId]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "ascending" ? "descending" : "ascending");
    } else {
      setSortColumn(column);
      setSortDirection("ascending");
    }
  };

  useInitialEffect(() => {
    ProjectStore.ensureLoaded();
  });

  useEffect(() => {
    const passOrFailResults = filteredResults.filter(
      r => r.status === TestStatus.PASS || r.status === TestStatus.FAIL
    );

    if (chartData.loaded && passOrFailResults.length > 0) {
      const chartDataPoints = passOrFailResults
        .filter(r => r.startTime)
        .map(r => ({
          x: new Date(r.startTime),
          y: (r.duration || 0) / 1000,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

      const statusColors = passOrFailResults
        .filter(r => r.startTime)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        .map(r =>
          r.status === TestStatus.PASS ? "#008000" : "#ed1d0a"
        );

      displayTimeLineChart("test-history-chart-canvas", [
        {
          label: "Duration (s)",
          data: chartDataPoints,
          borderColor: "transparent",
          backgroundColor: "transparent",
          fill: false,
          showLine: false,
          pointRadius: 6,
          pointBackgroundColor: statusColors,
        },
      ]);
      setShowChart(true);
    } else {
      setShowChart(false);
    }
  }, [chartData.loaded, filteredResults]);

  useEffect(() => {
    if (projectTestId) {
      AnalyticsStore.loadTestHistoryData(projectTestId, dateRange);
    }
  }, [projectTestId, dateRange]);

  const handleProjectChange = (id: number) => {
    setProjectId(id);
    setProjectTestId(null);
    setEnvironmentFilter(null);
    setBranchFilter(null);
    setStatusFilter(null);
    AnalyticsStore.testHistoryChartData.clear();
  };

  const handleTestChange = (id: number) => {
    setProjectTestId(id);
    setEnvironmentFilter(null);
    setBranchFilter(null);
    setStatusFilter(null);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setEnvironmentFilter(null);
    setBranchFilter(null);
    setStatusFilter(null);
  };

  const getStatusLabel = (status: TestStatus) => {
    switch (status) {
      case TestStatus.PASS:
        return <span style={{ color: "#008000", fontWeight: "bold" }}>PASS</span>;
      case TestStatus.FAIL:
        return <span style={{ color: "#ed1d0a", fontWeight: "bold" }}>FAIL</span>;
      default:
        return <span style={{ color: "#666" }}>{status}</span>;
    }
  };

  return (
    <div id="test-history-tab">
      <div className="list-filters no-offset">
        <ProjectSelect
          placeholder="Select a project"
          value={projectId}
          onChange={handleProjectChange}
          onlyWithTests
        />
        <ProjectTestSelect
          projectId={projectId}
          placeholder="Select a test"
          value={projectTestId}
          onChange={handleTestChange}
        />
      </div>
      <div className="list-filters no-offset">
        <Select
          className="date-range-select"
          placeholder="Date Range"
          value={dateRange ? dateRangeOptions.find(o => o.value === dateRange) : null}
          onChange={(option) => handleDateRangeChange(option?.value as DateRange)}
          options={dateRangeOptions}
          isClearable
          disabled={!projectTestId}
        />
        <Select
          className="environment-select"
          placeholder="All Environments"
          value={environmentFilter ? { label: environmentFilter, value: environmentFilter } : null}
          onChange={(option) => setEnvironmentFilter(option?.value || null)}
          options={uniqueEnvironments}
          isClearable
          isSearchable
          disabled={!projectTestId || uniqueEnvironments.length === 0}
          maxOptions={10}
        />
        <Select
          className="branch-select"
          placeholder="All Branches"
          value={branchFilter ? { label: branchFilter, value: branchFilter } : null}
          onChange={(option) => setBranchFilter(option?.value || null)}
          options={uniqueBranches}
          isClearable
          isSearchable
          maxOptions={10}
          disabled={!projectTestId || uniqueBranches.length === 0}
        />
        <Select
          className="status-select"
          placeholder="All Statuses"
          value={statusFilter ? { label: statusFilter.replace("_", " "), value: statusFilter } : null}
          onChange={(option) => setStatusFilter(option?.value || null)}
          options={statusOptions}
          isClearable
          disabled={!projectTestId}
        />
      </div>
      <br />

      {chartData.loading && (
        <div className="loading">
          <Spinner size={30} />
        </div>
      )}

      {!projectTestId && !chartData.loading && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          Select a project and test to view test history
        </div>
      )}

      {chartData.loaded && projectTestId && data.results.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          No test history found for the selected test
        </div>
      )}

      {chartData.loaded && data.results.length > 0 && (
        <>
          <Card.Group centered>
            <Card>
              <Card.Content>
                <div className="name">Total Runs</div>
                <div className="value">{numberWithCommas(filteredResults.length)}</div>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <div className="name">Passed</div>
                <div className="value" style={{ color: "#008000" }}>
                  {numberWithCommas(filteredResults.filter(r => r.status === TestStatus.PASS).length)}
                </div>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <div className="name">Failed</div>
                <div className="value" style={{ color: "#ed1d0a" }}>
                  {numberWithCommas(filteredResults.filter(r => r.status === TestStatus.FAIL).length)}
                </div>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <div className="name">Pass Rate</div>
                <div className="value">
                  {filteredResults.length > 0
                    ? Math.round((filteredResults.filter(r => r.status === TestStatus.PASS).length / filteredResults.length) * 100)
                    : 0}%
                </div>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <div className="name">Avg Duration</div>
                <div className="value">
                  {filteredResults.length > 0
                    ? (filteredResults.reduce((sum, r) => sum + (r.duration || 0), 0) / filteredResults.length / 1000).toFixed(2)
                    : 0} s
                </div>
              </Card.Content>
            </Card>
          </Card.Group>

          <div className={`chart ${showChart ? "" : "hide"}`} style={{ marginTop: "20px" }}>
            <h4 style={{ textAlign: "center" }}>Duration Over Time (seconds, colored by status: green=pass, red=fail)</h4>
            <canvas id="test-history-chart-canvas"></canvas>
          </div>

          <div style={{ marginTop: "30px" }}>
            <h4>Test Details</h4>
            <Table celled striped sortable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    sorted={sortColumn === "startTime" ? sortDirection : null}
                    onClick={() => handleSort("startTime")}
                  >
                    Test Run
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === "status" ? sortDirection : null}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === "duration" ? sortDirection : null}
                    onClick={() => handleSort("duration")}
                  >
                    Duration (s)
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === "environment" ? sortDirection : null}
                    onClick={() => handleSort("environment")}
                  >
                    Environment
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === "branch" ? sortDirection : null}
                    onClick={() => handleSort("branch")}
                  >
                    Branch
                  </Table.HeaderCell>
                  <Table.HeaderCell>Test Suite</Table.HeaderCell>
                  <Table.HeaderCell>Git Ref</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paginatedResults.map((result, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <Link to={organizationUrl(`test-runs/${result.testRunId}`)} style={{ color: "#2563eb" }}>
                        {result.startTime ? format(new Date(result.startTime), "MM/dd/yyyy, hh:mm:ss a") : `Run ${result.testRunId}`}
                      </Link>
                    </Table.Cell>
                    <Table.Cell>{getStatusLabel(result.status)}</Table.Cell>
                    <Table.Cell>{((result.duration || 0) / 1000).toFixed(2)}</Table.Cell>
                    <Table.Cell>{result.environment || "-"}</Table.Cell>
                    <Table.Cell>{result.branch || "-"}</Table.Cell>
                    <Table.Cell>{result.testSuite || "-"}</Table.Cell>
                    <Table.Cell>
                      {result.gitRef && selectedProject ? (
                        <RepoLink project={selectedProject} gitRef={result.gitRef} newWindow />
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginTop: "15px", gap: "10px" }}>
                <Button
                  icon="chevron left"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                <span>Page {currentPage} of {totalPages}</span>
                <Button
                  icon="chevron right"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

export default TestHistoryTab;
